"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { CdmState } from "@/components/cdm/CdmState";
import { SignDocumentModal } from "@/components/sign/SignDocumentModal";
import type { SignerBlock } from "@/lib/sign/signerParties";
import { useUserActions } from "@/lib/userActions/UserActionsContext";
import { useTranslations } from "@/lib/compat/i18n";

const DEFAULT_EINVOICE_SIGNER: SignerBlock = {
  signedAs:    "Aurora Aves Brasil S.A.",
  signerOrg:   "Seller (Exporter · Issuer) · BR",
  actionLabel: "Sign as Aurora Aves Brasil S.A.",
};

/**
 * Screen 2.4 — Invoice FSM Live. The Commercial Invoice tile walks the
 * Invoice FSM with the canonical CDM state token visible bottom-left.
 * Border colour steps through brand-palette luminance tiers per state.
 *
 * Hover the tile and the signature block appears: signing DID, signed-at
 * timestamp, public-key fingerprint matching the HCS-anchored verification
 * method from Act 1.5. This closes the loop on the e-Invoicing Corp DID
 * anchor.
 *
 * Each step's `peppol` field maps the state to the underlying Peppol /
 * PINT AE lifecycle stage. States with `extension: true` are Orbitra
 * extensions over the standard lifecycle — the audience can hover each
 * chip to see exactly which standard state Orbitra is extending. Reducer
 * state names stay unprefixed (scenarios JSON uses the bare strings).
 */
type FsmStep = {
  state:     "DRAFT" | "TRANSMITTED" | "ACCEPTED" | "EXTERNALLY_VALIDATED" | "SINGLE_WINDOW_SUBMITTED";
  t:         string;
  display:   string;
  /** Underlying Peppol / PINT AE lifecycle stage this Orbitra state
   *  maps to. Surfaced in the tooltip so the audience can see what
   *  extends what without us hand-waving "ORB.*" as proprietary. */
  peppol:    string;
  extension: boolean;
};

const STEPS: ReadonlyArray<FsmStep> = [
  { state: "DRAFT",                   t: "draft",                   display: "DRAFT",                peppol: "Peppol · DRAFT (pre-transmit)",                        extension: false },
  { state: "TRANSMITTED",             t: "transmitted",             display: "TRANSMITTED",          peppol: "Peppol AS4 · DELIVERED → buyer's access point",        extension: false },
  { state: "ACCEPTED",                t: "accepted",                display: "ACCEPTED",             peppol: "Peppol PINT AE · ACCEPTED (buyer/agent acknowledged)", extension: false },
  { state: "EXTERNALLY_VALIDATED",    t: "externally validated",    display: "ORB · EXT-VALIDATED",  peppol: "Orbitra extension over PINT AE Accepted · 3rd-party validator countersign (cross-doc consistency, weight tolerance)", extension: true },
  { state: "SINGLE_WINDOW_SUBMITTED", t: "single-window submitted", display: "ORB · SINGLE-WINDOW",  peppol: "Orbitra extension over PINT AE Accepted · invoice forwarded to corridor single-window (Dubai Trade / Receita Federal)", extension: true },
];

export function InvoiceFsmTile({
  invoiceState = "DRAFT",
  invoiceId = "INV-2026-0042",
  einvoiceSigner = DEFAULT_EINVOICE_SIGNER,
  einvoicePdf = "/sign-docs/einvoice.pdf",
  invoiceSignerHeader,
  requireSignCta = false,
  ctaActionKey,
  className,
}: {
  invoiceState?: typeof STEPS[number]["state"];
  invoiceId?: string;
  einvoiceSigner?: SignerBlock;
  einvoicePdf?: string;
  // Header line above the invoice id ("PINT AE invoice · signed by Aurora's Corp DID").
  // Optional override for T3 (UAE FTA · Gulf Phosphate Corp DID).
  invoiceSignerHeader?: string;
  /** When true, the e-invoice sign popup does NOT auto-open when the FSM
   *  reaches TRANSMITTED. Instead a "Sign with your OrbitraID" CTA
   *  overlay shows on land; clicking it dismisses the CTA and opens the
   *  sign popup. Used on T2's 4.8. */
  requireSignCta?: boolean;
  /** Optional UserActions key — when supplied, the CTA fired state
   *  survives Shift+N replay (persists via useUserActions context). */
  ctaActionKey?: string;
  className?: string;
}) {
  const userActions = useUserActions();
  const ctaAlreadyFired = ctaActionKey ? userActions.has(ctaActionKey) : false;
  const stepIdx = STEPS.findIndex((s) => s.state === invoiceState);
  const transmittedIdx = STEPS.findIndex((s) => s.state === "TRANSMITTED");

  // Sign popup: auto-open the moment the SSE projection tries to leave
  // DRAFT (i.e. when the FSM reaches TRANSMITTED). Aurora — the seller —
  // signs her invoice; the Sign click visibly causes the transmission,
  // mirroring the human-sign-before-handshake pattern used on Screen 2.1.
  // When `requireSignCta` is on, the auto-fire is suppressed — a CTA
  // overlay gates the sign popup instead. If the CTA was previously
  // fired (persisted UserActions), skip the gate on remount.
  const reachedTransmitted = stepIdx >= transmittedIdx;
  const [einvoiceOpen, setEinvoiceOpen] = useState(false);
  const [autoFired, setAutoFired] = useState(false);
  const [userSigned, setUserSigned] = useState(false);
  const [ctaOpen, setCtaOpen] = useState(requireSignCta && !ctaAlreadyFired);
  // Preview-only modal that surfaces the e-invoice PDF without firing
  // the sign chain. Used by the "View e-invoice" secondary button on
  // the CTA overlay so the audience can read the invoice before signing.
  const [previewOpen, setPreviewOpen] = useState(false);
  const chainCompletedRef = useRef(false);

  useEffect(() => {
    if (requireSignCta) return;
    if (reachedTransmitted && !autoFired) {
      setEinvoiceOpen(true);
      setAutoFired(true);
    }
  }, [reachedTransmitted, autoFired, requireSignCta]);

  // While the popup is still open / unsigned, freeze the rendered FSM walk
  // at DRAFT so the audience sees Aurora's Sign click drive the transition
  // — not the other way around. After she signs (or dismisses), the walk
  // catches up to wherever the SSE projection has reached.
  const displayedStepIdx = userSigned ? stepIdx : Math.min(stepIdx, transmittedIdx - 1);
  const displayedInvoiceState: typeof STEPS[number]["state"] =
    userSigned ? invoiceState : (displayedStepIdx >= 0 ? STEPS[displayedStepIdx].state : "DRAFT");
  const t = useTranslations("invoiceFsmTile");

  return (
    <div className={clsx("grid h-full grid-cols-3 gap-4", className)}>
      <div className="glass col-span-2 flex min-w-0 flex-col p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{invoiceSignerHeader ?? t("defaultInvoiceHeader")}</p>
            <h3 className="mt-1 font-display text-lg font-semibold text-orbitra-paper" dir="ltr">{invoiceId}</h3>
          </div>
          <CdmState token={displayedInvoiceState} />
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3 text-[12px]">
          <Field label={t("fieldBuyer")}         value={t("buyerT1Value")} />
          <Field label={t("fieldSeller")}        value={t("sellerT1Value")} />
          <Field label={t("fieldHsCode")}        value={t("hsCodeT1")} />
          <Field label={t("fieldQuantity")}      value={t("quantityT1")} />
          <Field label={t("fieldGrossAmount")}   value={t("grossAmountT1")} />
          <Field label={t("fieldCurrency")}      value="USD" />
          <Field label={t("fieldTaxCategory")}   value={t("taxCategoryT1")} />
          <Field label={t("fieldContractRef")}   value={t("contractRefT1")} />
        </div>

        <div className="mt-auto pt-4">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("fsmWalkLabel")}</p>
          {/* flex-wrap + reduced connector width so the long state names
           *  (EXTERNALLY_VALIDATED, SINGLE_WINDOW_SUBMITTED) wrap to a
           *  second row when the col-span-2 is narrow rather than spilling
           *  into the right pane. The wrapping arrow connectors are drawn
           *  inside each step so they wrap with their owning chip. */}
          <div className="mt-2 flex flex-wrap items-center gap-y-1.5">
            {STEPS.map((s, i) => (
              <div key={s.state} className="flex items-center">
                <span
                  className={clsx(
                    "rounded-md border px-2 py-1 font-mono text-[10px] uppercase tracking-[0.06em] cursor-help",
                    i <= displayedStepIdx
                      ? i === STEPS.length - 1
                        ? "border-state-financed/50 bg-state-financed/15 text-state-financed"
                        : "border-state-traced/50 bg-state-traced/15 text-state-traced"
                      : "border-state-future/40 bg-state-future/5 text-state-future/80",
                  )}
                  title={s.extension
                    ? `${t("chipHelperExtension")} ${s.peppol}`
                    : `${t("chipHelperStandard")} ${s.peppol}`}
                >
                  {s.display}
                </span>
                {i < STEPS.length - 1 && (
                  <span
                    aria-hidden="true"
                    className={clsx(
                      "mx-1.5 font-mono text-[10px]",
                      i < displayedStepIdx ? "text-state-traced" : "text-orbitra-muted/30",
                    )}
                  >
                    →
                  </span>
                )}
              </div>
            ))}
          </div>
          <p className="mt-2 font-mono text-[9px] text-orbitra-muted">
            <span className="text-orbitra-paper">ORB · *</span> {t("fsmFooter")}
          </p>
        </div>
      </div>

      <div className="glass flex flex-col p-5">
        <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("signatureBlockTitle")}</p>
        <div className="mt-3 space-y-2 text-[11px]">
          <SigField label={t("signingDidLabel")}         value="did:orb:corbid:5493-aves-aurora-br" />
          <SigField label={t("algorithmLabel")}          value="Ed25519" />
          <SigField label={t("publicKeyLabel")}          value="9f5c · b21d · 4e83" />
          <SigField label={t("signedAtLabel")}           value="2026-05-15 09:14:22 UTC" />
          <SigField label={t("ledgerVerificationLabel")} value="ledger-seq 41" />
        </div>

        <div className="mt-auto rounded-md border border-state-verified/30 bg-orbitra-ink/40 p-3 text-[10px]">
          <p className="font-mono text-state-verified">{t("verifiedTag")}</p>
          <p className="mt-1 text-orbitra-teal-soft">
            {t("verifiedFooter")}
          </p>
        </div>
      </div>

      <SignDocumentModal
        id="einvoice"
        open={einvoiceOpen}
        onOpen={() => setEinvoiceOpen(true)}
        onClose={() => {
          setEinvoiceOpen(false);
          if (!chainCompletedRef.current) {
            chainCompletedRef.current = true;
            setUserSigned(true);
          }
        }}
        pdfPath={einvoicePdf}
        documentTitle={t("documentTitleEinvoice")}
        documentRef={invoiceId}
        signedAs={einvoiceSigner.signedAs}
        signerOrg={einvoiceSigner.signerOrg}
        actionLabel={einvoiceSigner.actionLabel}
      />

      {ctaOpen && typeof document !== "undefined" && createPortal(
        // Portal: escape .scene-fade so the CTA centres on the viewport.
        <div className="fixed inset-0 z-30 grid place-items-center bg-orbitra-ink/55 backdrop-blur-sm">
          <div className="glass w-[min(420px,90vw)] p-6 text-center">
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-verified">
              {t("ctaKicker")}
            </p>
            <h3 className="mt-2 font-display text-base font-semibold text-orbitra-paper">
              {t("ctaTitle")}
            </h3>
            <p className="mt-2 text-[11px] text-orbitra-muted">
              {t("ctaBody")}
            </p>
            <button
              type="button"
              autoFocus
              onClick={() => {
                setCtaOpen(false);
                setEinvoiceOpen(true);
                setAutoFired(true);
                if (ctaActionKey) userActions.fire(ctaActionKey);
              }}
              className="cta-primary mt-4 w-full px-5 py-2 font-display text-sm font-semibold"
            >
              {t("ctaSignButton")}
            </button>
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="mt-2 w-full rounded border border-state-verified/40 px-5 py-2 font-mono text-[11px] uppercase tracking-wider text-state-verified hover:bg-state-verified/10"
            >
              {t("ctaViewButton")}
            </button>
          </div>
        </div>,
        document.body,
      )}

      <SignDocumentModal
        id="einvoice-preview"
        open={previewOpen}
        onOpen={() => setPreviewOpen(true)}
        onClose={() => setPreviewOpen(false)}
        pdfPath={einvoicePdf}
        documentTitle={t("documentTitleEinvoice")}
        documentRef={invoiceId}
        signedAs={einvoiceSigner.signedAs}
        signerOrg={einvoiceSigner.signerOrg}
        viewOnly
        actionLabel={t("closePreviewButton")}
      />
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-state-verified/20 bg-orbitra-ink/40 px-3 py-2">
      <p className="text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{label}</p>
      <p className="mt-0.5 text-orbitra-paper">{value}</p>
    </div>
  );
}

function SigField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{label}</span>
      <span className="truncate font-mono text-[11px] text-orbitra-paper">{value}</span>
    </div>
  );
}
